import { isEmpty } from 'lodash';
import { LocationChangedAction, replace } from 'redux-little-router';
import { combineEpics } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { filter, map, mapTo, mergeMap, switchMap } from 'rxjs/operators';

import { Action } from '../actions';
import { getBannersForEdit, getBaseInfoForBannerCreation, resetBannersEdit } from '../actions/bannersEdit';
import { openInternalBannersEditAction } from '../actions/bannersEdit/internal/openInternalBannersEdit';
import {
    getCampaignsForEdit,
    getDefaultCampaignsForEdit,
    getRetargetingConditionsForCampaignsEdit
} from '../actions/campaignsEdit';
import { requestClientMobileAppsData } from '../actions/clientMobileAppsData';
import {
    changeLocationHard,
    fetchCommonData,
    hideRecommendationsTooltip,
    updateDocumentTitle,
    updateStringDicts
} from '../actions/common';
import { updateDealsSourceParams } from '../actions/deals';
import { fetchFreelancerCard } from '../actions/freelancers/freelancer-card';
import { fetchInitialFreelancerData } from '../actions/freelancers/freelancer-card-edit';
import { fetchCustomersData } from '../actions/freelancers/freelancer-customers';
import { fetchFreelancersListData } from '../actions/freelancers/freelancers-list';
import { fetchGoalsData } from '../actions/goals';
import { requestGridRow, updateCalcTypeFilter } from '../actions/grid-screen';
import {
    getCampaignBasicData,
    getGroupsForEditAction,
    getRetargetingConditionsForGroupsEdit
} from '../actions/groupsEdit';
import { onLogSourceParamsChange, requestLogRow } from '../actions/log';
import { loadInternalMarketerAction } from '../actions/marketer/internal/loadInternalMarketer';
import { loadInternalMarketersAction } from '../actions/marketers/internal/loadInternalMarketers';
import { requestMinusPhraseLibraryGridRow } from '../actions/minusPhraseLibrary';
import { fetchApp } from '../actions/mobile-app';
import { updatePricePackagesSourceParams } from '../actions/pricePackages';
import { getPricePackageDataForCreation, getPricePackagesForEdit } from '../actions/pricePackagesEdit';
import { loadInternalProductsAction } from '../actions/products/internal/loadInternalProducts';
import { fetchRecommendations } from '../actions/recommendations';
import { fetchRecommendationsGridSummary, fetchRecommendationsItems } from '../actions/recommendations-grid';
import { fetchReports } from '../actions/reportsClient';
import { fetchReportsDomain } from '../actions/reportsDomain';
import { fetchAppTrackersTemplates } from '../actions/trackingParametersEditor';
import { getUserSettingsData } from '../actions/userSettings';
import { getProductsTitle } from '../components/Products/Products';
import { awaitCommonData, ofNewVisit, ofRoute } from '../operators';
import { RoutesPath } from '../routes/index';
import { selectClientId } from '../selectors/common';
import { selectNormalizedDealsSourceParams } from '../selectors/deals';
import { selectFreelancersListSortParams } from '../selectors/freelancers';
import { selectLogSourceParams } from '../selectors/log';
import { selectNormalizedPricePackagesSourceParams } from '../selectors/pricePackages/pricePackages';
import { selectRecommendationItemsSortParams } from '../selectors/recommendations';
import { checkIsCampaignAddAllowedInDna } from '../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { castStringToEnum } from '../service/CastHelpers';
import { preparePeriodFilterAfterLocalStorage, serializePeriodFilter } from '../service/FilterHelpers/FilterHelpers';
import { Session } from '../service/Session/Session';
import { UrlHelpers } from '../service/UrlHelpers';
import { CampaignsCalcTypeFilter } from '../typings/domain';
import { DnaEpic, DnaEpicAction } from '../typings/epic';
import { GridScreenUrlSearchQueryParams, GridScreenUrlSearchRouteParams } from '../typings/grid';
import { RecommendationType } from '../typings/recommendations';
import { RegionInputIds } from '../typings/regions';
import { MOBILE_APP_ID } from '../typings/trackingParametersEditor';
import { getVideoCreatorDataForEdit } from '../video-creator/VideoCreatorEditPage/actions';
import { getVideoCreatorMyVideoList } from '../video-creator/VideoCreatorMyVideoPage/actions';
import { getVideoCreatorTemplateList } from '../video-creator/VideoCreatorTemplatesListPage/actions';

const campaignsEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.campaignsEdit),
        ofNewVisit(store$),
        awaitCommonData(action$, store$),
        switchMap(
            (): DnaEpicAction => {
                const state = store$.value;
                const {
                    editCampaigns: { isCreatePage, campaignType },
                    commonData: { login, operatorFeatures, features: clientFeatures }
                } = state;

                const redirectAction = [changeLocationHard(UrlHelpers.getCampaignListUrl({ ulogin: login }))];
                const resultActions: Action[] = [getRetargetingConditionsForCampaignsEdit(login)];

                if (isCreatePage) {
                    if (!campaignType) {
                        throw new Error('Не указан тип при создании новой кампании');
                    }

                    const isCampaignAddAllowed = checkIsCampaignAddAllowedInDna(
                        campaignType,
                        operatorFeatures,
                        clientFeatures
                    );

                    if (!isCampaignAddAllowed) {
                        return redirectAction;
                    }

                    resultActions.push(getDefaultCampaignsForEdit(campaignType));
                } else {
                    resultActions.push(getCampaignsForEdit());
                }

                return resultActions;
            }
        )
    );
};

const groupsEditRouteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.groupsEdit),
        awaitCommonData(action$, store$),
        filter((action: LocationChangedAction) => {
            const { route } = action.payload;
            const { previous, query } = store$.value.router;

            return (
                (query !== undefined && query.forceGroupsEditNewVisit === '1') || route !== (previous && previous.route)
            );
        }),
        mergeMap(
            (): Observable<Action> => {
                const {
                    commonData: { stringDicts, accessRights }
                } = store$.value;

                return from(
                    deps.groupsDataProvider.getUaTraits(stringDicts, accessRights && accessRights.canEditInternalAds)
                ).pipe(
                    mergeMap(stringDictsResult => {
                        return [updateStringDicts(stringDictsResult)];
                    })
                );
            }
        ),
        switchMap(updateStringDictsAction => {
            const { login } = store$.value.commonData;
            const {
                editGroups: { campaignsIds, isNew }
            } = store$.value;
            const observables: Action[] = [fetchCommonData()];

            if (isNew) {
                observables.push(getCampaignBasicData(login, campaignsIds[0]));
                observables.push(updateStringDictsAction);
            } else {
                observables.push(getGroupsForEditAction(login));
                observables.push(getRetargetingConditionsForGroupsEdit(login));
            }

            return observables;
        })
    );
};

const userSettingsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.userSettings),
        awaitCommonData(action$, store$),
        ofNewVisit(store$),
        switchMap(() => {
            return [getUserSettingsData()];
        })
    );
};

const bannersEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.bannersEdit),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            const {
                editBanners: { campaignsIds, groupsIds, isNew }
            } = store$.value;
            const observables: Action[] = [fetchCommonData()];

            if (isNew) {
                observables.push(getBaseInfoForBannerCreation(login, campaignsIds, groupsIds));
            } else {
                observables.push(getBannersForEdit(login));
            }

            return observables;
        })
    );
};

const internalBannersEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.internalBannersEdit),
        ofNewVisit(store$),
        switchMap(() => {
            return [fetchCommonData(), openInternalBannersEditAction()];
        })
    );
};

const freelancersListRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancersList),
        ofNewVisit(store$),
        switchMap((action: LocationChangedAction) => {
            const state = store$.value;
            const { login } = store$.value.commonData;
            const { query = {} } = action.payload;
            const sortParams = selectFreelancersListSortParams(state);
            const freelancerLogins = query.freelancerLogins ? query.freelancerLogins.split(',') : undefined;

            return of(fetchFreelancersListData(login, sortParams, freelancerLogins));
        })
    );
};

const freelancerCardRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancerCard),
        ofNewVisit(store$),
        switchMap(
            (action: LocationChangedAction): Observable<Action> => {
                const { params = {} } = action.payload;
                const freelancerLogin = params.freelancerLogin;
                if (!freelancerLogin) {
                    return of(replace(UrlHelpers.getFreelancersListUrl()));
                }

                const { login } = store$.value.commonData;

                return of(fetchFreelancerCard(freelancerLogin, login));
            }
        )
    );
};

const freelancersEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancersEdit),
        ofNewVisit(store$),
        switchMap(
            (action: LocationChangedAction): Observable<Action> => {
                const { params = {} } = action.payload;
                const freelancerLogin = params.freelancerLogin;
                if (!freelancerLogin) {
                    return of(replace(UrlHelpers.getFreelancersListUrl()));
                }

                return of(fetchInitialFreelancerData(freelancerLogin, RegionInputIds.FreelancerRegionInputId));
            }
        )
    );
};

const freelancerCustomersRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.freelancerCustomers),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            return of(fetchCustomersData(login));
        })
    );
};

const recommendationsPageRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.recommendationsPage),
        ofNewVisit(store$),
        switchMap(() => [fetchRecommendations(), hideRecommendationsTooltip()])
    );
};

const recommendationsItemsRouteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.recommendationsItems),
        switchMap((action: LocationChangedAction) => {
            const state = store$.value;
            const {
                commonData: { login },
                router: { previous }
            } = state;
            const { params = {}, route } = action.payload;
            const recommendationType = castStringToEnum(params.type, RecommendationType);
            if (!recommendationType) {
                throw Error('Неизвестный тип рекомендации');
            }
            const sortParams = selectRecommendationItemsSortParams(state);

            if (previous && previous.route === route) {
                deps.segmentManager.reset();
                return of(fetchRecommendationsGridSummary(recommendationType, login));
            }

            return [
                fetchCommonData(),
                fetchRecommendationsItems(recommendationType, login, 0, sortParams),
                fetchRecommendationsGridSummary(recommendationType, login)
            ];
        })
    );
};

export const gridRouteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.grid, RoutesPath.shortGrid),
        switchMap((action: LocationChangedAction): Observable<Action> | Action[] => {
            const {
                router: { pathname, previous, query = {}, params = {} },
                grid: { filterDict = {} },
                commonData: { login }
            } = store$.value;

            const additionalActions: Action[] = [];

            // убрать updateCalcTypeFilter в DIRECT-98318
            if (previous && previous.route === RoutesPath.internalBannersEdit) {
                additionalActions.push(updateCalcTypeFilter(CampaignsCalcTypeFilter.CPC));
            }

            const queryFilter = query[GridScreenUrlSearchQueryParams.filter];
            const view = params[GridScreenUrlSearchRouteParams.view];
            const { gridFilter = {} } = Session.getValueFromCurrentSession(login);
            const gridLocalStorageFilterValue = gridFilter[view];
            const statPreset = query[GridScreenUrlSearchQueryParams.statPreset];
            const statFrom = query[GridScreenUrlSearchQueryParams.statFrom];
            const statTo = query[GridScreenUrlSearchQueryParams.statTo];
            const { statPeriod } = Session.getValueFromCurrentSession(login);
            let resultQuery = {};

            if (!queryFilter && filterDict[view] !== '' && gridLocalStorageFilterValue) {
                resultQuery = {
                    ...resultQuery,
                    filter: gridLocalStorageFilterValue
                };
            }

            // Если периода нету в URL, но он есть в LocalStorage
            if (statPeriod !== undefined && !statPreset && !statFrom && !statTo) {
                const serializedPeriod = serializePeriodFilter(preparePeriodFilterAfterLocalStorage(statPeriod));
                resultQuery = {
                    ...resultQuery,
                    ...serializedPeriod
                };
            }

            if (!isEmpty(resultQuery)) {
                return of(
                    replace(
                        {
                            pathname: pathname,
                            query: resultQuery
                        },
                        {
                            persistQuery: true
                        }
                    )
                );
            }

            deps.segmentManager.reset();

            if (previous && previous.route === action.payload.route) {
                return EMPTY;
            }

            // убрать additionalActions в DIRECT-98318
            return additionalActions.concat([requestGridRow(0), fetchGoalsData()]);
        })
    );
};

const clientReportsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.offlineReports),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            return [fetchCommonData(), fetchReports(login)];
        })
    );
};

const resetBannersEditEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.bannersEdit),
        ofNewVisit(store$),
        mapTo(resetBannersEdit())
    );
};

const domainReportsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.domainsReports),
        ofNewVisit(store$),
        mapTo(fetchReportsDomain())
    );
};

const mobileAppsListRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.mobileAppsList),
        ofNewVisit(store$),
        mapTo(requestClientMobileAppsData())
    );
};

const mobileAppsCreateRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.mobileAppsCreate),
        ofNewVisit(store$),
        switchMap(() => {
            const { login } = store$.value.commonData;
            return [requestClientMobileAppsData(), fetchAppTrackersTemplates(MOBILE_APP_ID, login)];
        })
    );
};

const mobileAppsEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.mobileAppsEdit),
        ofNewVisit(store$),
        switchMap((action: LocationChangedAction) => {
            const { login } = store$.value.commonData;
            const { params = {} } = action.payload;
            const mobileAppId = params.mobileAppId || '0';

            return [requestClientMobileAppsData(), fetchApp(mobileAppId, login)];
        })
    );
};

const dealsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.deals),
        switchMap(() => {
            const state = store$.value;
            const dealSourceParams = selectNormalizedDealsSourceParams(state);

            return [fetchCommonData(), updateDealsSourceParams(dealSourceParams)];
        })
    );
};

const pricePackagesRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.pricePackagesList),
        switchMap(() => {
            const state = store$.value;
            const pricePackagesSourceParams = selectNormalizedPricePackagesSourceParams(state);

            return [fetchCommonData(), updatePricePackagesSourceParams(pricePackagesSourceParams)];
        })
    );
};

const pricePackagesEditRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.pricePackagesEdit),
        switchMap(() => {
            const {
                editPricePackages: { isNew }
            } = store$.value;
            const observables: Array<Action> = [fetchCommonData()];

            if (isNew) {
                observables.push(getPricePackageDataForCreation());
            } else {
                observables.push(getPricePackagesForEdit());
            }

            return observables;
        })
    );
};

const productsRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.products),
        ofNewVisit(store$),
        switchMap(() => {
            return of(loadInternalProductsAction());
        })
    );
};

const productsRouteTitleEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.products),
        ofNewVisit(store$),
        awaitCommonData(action$, store$),
        switchMap(() => {
            const title = getProductsTitle(store$.value.commonData.accessRights);

            if (title) {
                return of(updateDocumentTitle(title));
            }

            return EMPTY;
        })
    );
};

const marketersRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.marketers),
        ofNewVisit(store$),
        switchMap(() => {
            return of(loadInternalMarketersAction());
        })
    );
};

const marketerCardRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.marketerCard),
        ofNewVisit(store$),
        switchMap(() => {
            const { params = {} } = store$.value.router;

            return of(loadInternalMarketerAction(params.id));
        })
    );
};

const minusPhrasesLibraryEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.minusPhrasesLibrary),
        ofNewVisit(store$),
        switchMap(
            (): Observable<Action> => {
                deps.segmentManager.reset();

                return of(requestMinusPhraseLibraryGridRow(0));
            }
        )
    );
};

const onLogSourceParamsChangedEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.log),
        map(
            (action: LocationChangedAction): Action => {
                const sourceParams = selectLogSourceParams(store$.value);

                return onLogSourceParamsChange(sourceParams);
            }
        )
    );
};

const logRouteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.log),
        ofNewVisit(store$),
        map(
            (action: LocationChangedAction): Action => {
                deps.segmentManager.reset();

                return requestLogRow(0);
            }
        )
    );
};

const videoCreatorEditEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofRoute(RoutesPath.videoCreatorEdit),
        ofNewVisit(store$),
        awaitCommonData(action$, store$),
        switchMap(
            (): Observable<Action> => {
                return of(getVideoCreatorDataForEdit());
            }
        )
    );
};

const videoConstructorMyVideoRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.videoCreatorMyVideo),
        ofNewVisit(store$),
        awaitCommonData(action$, store$),
        switchMap(() => {
            const state = store$.value;
            const clientId = selectClientId(state);

            return [getVideoCreatorMyVideoList(clientId)];
        })
    );
};

const videoConstructorMyTemplateListRouteEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofRoute(RoutesPath.videoCreatorTemplatesList),
        ofNewVisit(store$),
        awaitCommonData(action$, store$),
        switchMap(() => {
            return [getVideoCreatorTemplateList()];
        })
    );
};

export const routerLocationChangedEpic = combineEpics(
    campaignsEditRouteEpic,
    groupsEditRouteEpic,
    bannersEditRouteEpic,
    userSettingsRouteEpic,
    internalBannersEditRouteEpic,
    freelancersListRouteEpic,
    logRouteEpic,
    freelancerCardRouteEpic,
    onLogSourceParamsChangedEpic,
    freelancersEditRouteEpic,
    freelancerCustomersRouteEpic,
    recommendationsPageRouteEpic,
    recommendationsItemsRouteEpic,
    gridRouteEpic,
    clientReportsRouteEpic,
    resetBannersEditEpic,
    domainReportsRouteEpic,
    mobileAppsListRouteEpic,
    mobileAppsCreateRouteEpic,
    mobileAppsEditRouteEpic,
    dealsRouteEpic,
    productsRouteEpic,
    marketersRouteEpic,
    marketerCardRouteEpic,
    minusPhrasesLibraryEpic,
    productsRouteTitleEpic,
    pricePackagesRouteEpic,
    pricePackagesEditRouteEpic,
    videoConstructorMyVideoRouteEpic,
    videoConstructorMyTemplateListRouteEpic,
    videoCreatorEditEpic
);
